import { useCallback, useEffect, useState } from "react";
import { Badge, Button, Card, Form, Table } from "react-bootstrap";
import {
  getSchedulerState,
  setSchedulerConfig,
  setSchedulerTask,
  runSchedulerTask,
  clearSchedulerRunLog,
} from "../../services/apiClient.js";
import { formatDateTime } from "../../utils/date.js";

// System Data → Scheduler (owner only): the after-hours task list.
//
// Ships DORMANT — master off, every task off — so this panel is where an
// environment DECLARES it runs tasks: dev off, Plex demo off, live on
// (Robert 2026-08-20). "Run now" ignores the switches on purpose: manual
// means now, and it is how a dev box with everything off proves a task works.
//
// The runs column is the backup script's LAST_RESULT lesson: what matters is
// "did it run", not just "were there errors". Successes collapse to one fresh
// row; failures persist (and land in the Error Log as CRITICAL) until deleted.
// "in 10h 26m" — computed from the server's next_run_at timestamp, so the
// countdown is honest in any viewer timezone (Robert watches from Brisbane;
// the run time is NZ wall clock).
function untilText(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 60_000) return "within the next minute";
  const mins = Math.round(ms / 60_000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `in ${h > 0 ? `${h}h ` : ""}${m}m`;
}

// A run's task-shaped result, rendered usefully: a `jobs` array becomes
// clickable claim numbers (URLs carry the number); anything else prints as
// compact JSON — the blob is the contract, the rendering is best-effort.
function RunResult({ result }) {
  if (!result || typeof result !== "object") return null;
  const { jobs, ...rest } = result;
  return (
    <div className="small">
      {Array.isArray(jobs) && jobs.length > 0 ? (
        <span>
          {jobs.map((n, i) => (
            <span key={n}>
              {i > 0 ? ", " : ""}
              <a href={`/claims/${n}`} target="_blank" rel="noreferrer">{n}</a>
            </span>
          ))}
        </span>
      ) : null}
      {Object.keys(rest).length > 0 ? (
        <code className="ms-2 text-muted">{JSON.stringify(rest)}</code>
      ) : null}
    </div>
  );
}

function SchedulerPanel() {
  const [state, setState] = useState(null);
  const [error, setError] = useState("");
  const [busyKey, setBusyKey] = useState(null); // task key while a manual run is in flight
  const [runTime, setRunTime] = useState("");
  const [historyKey, setHistoryKey] = useState(null); // task whose run log is expanded

  const load = useCallback(() => {
    getSchedulerState()
      .then((s) => {
        setState(s);
        setRunTime(s.config?.run_time || "02:00");
        setError("");
      })
      .catch((err) => setError(err.response?.data?.error || "Unable to load the scheduler."));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function patchConfig(patch) {
    try {
      setState(await setSchedulerConfig(patch));
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Unable to update the scheduler.");
    }
  }

  async function toggleTask(key, enabled) {
    try {
      setState(await setSchedulerTask(key, enabled));
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Unable to update the task.");
    }
  }

  // The manual broom (Robert 2026-08-24, intended maybe twice a year): every
  // row goes, kept-forever history included — that is what manual means —
  // except each task's newest run, so "last ran" never goes blank. The
  // confirm spells that out because the noteworthy rows are the one thing
  // the scheduled tidy deliberately protects.
  async function clearLog() {
    if (
      !window.confirm(
        "Delete the entire run log — including runs kept as history — except the newest run of each task?"
      )
    )
      return;
    setBusyKey("clear-log");
    setError("");
    try {
      setState(await clearSchedulerRunLog());
    } catch (err) {
      setError(err.response?.data?.error || "Unable to clear the run log.");
    } finally {
      setBusyKey(null);
    }
  }

  async function runNow(key) {
    setBusyKey(key);
    setError("");
    try {
      await runSchedulerTask(key);
    } catch (err) {
      setError(err.response?.data?.error || "The task run failed.");
    } finally {
      setBusyKey(null);
      load(); // outcome (success or failure) is in the runs either way
    }
  }

  if (!state) {
    return <div className="text-muted">{error || "Loading scheduler…"}</div>;
  }

  return (
    <div>
      <h5 className="mb-1">After-hours Scheduler</h5>
      <p className="text-muted small mb-3">
        Runs the tasks below once a day, in order. A failed task is recorded here and in the
        Error Log as <strong>CRITICAL</strong>, and the next task still runs. Everything ships
        switched off — this panel is where an environment opts in.
      </p>

      {error ? <div className="alert alert-danger py-2">{error}</div> : null}

      <Card className="mb-3">
        <Card.Body className="d-flex flex-wrap align-items-center column-gap-4 row-gap-2 py-2">
          <Form.Check
            type="switch"
            id="scheduler-enabled"
            label="Scheduler on"
            checked={!!state.config?.enabled}
            onChange={(e) => patchConfig({ enabled: e.target.checked })}
          />
          <div className="d-flex align-items-center gap-2">
            <Form.Label className="mb-0 small fw-semibold">Runs daily at</Form.Label>
            <Form.Control
              size="sm"
              style={{ width: 90 }}
              value={runTime}
              onChange={(e) => setRunTime(e.target.value)}
              onBlur={() => {
                if (runTime !== state.config?.run_time) patchConfig({ run_time: runTime });
              }}
              placeholder="02:00"
            />
            <span className="small text-muted">NZ time, 24-hour</span>
          </div>
          <div className="small text-muted">
            Last ran: {state.config?.last_run_on || "never"}
            {state.config?.enabled && state.config?.next_run_at ? (
              <> · Next run: <strong>{untilText(state.config.next_run_at)}</strong></>
            ) : null}
          </div>
          <Button
            size="sm"
            variant="outline-secondary"
            className="ms-auto"
            disabled={busyKey === "clear-log"}
            onClick={clearLog}
          >
            {busyKey === "clear-log" ? "Clearing…" : "Clear log (keep last run of each task)"}
          </Button>
        </Card.Body>
      </Card>

      <Table bordered size="sm" className="align-middle">
        <thead className="table-surface">
          <tr>
            <th style={{ width: 70 }}>On</th>
            <th>Task</th>
            <th>Last successful run</th>
            <th style={{ width: 110 }} />
          </tr>
        </thead>
        <tbody>
          {state.tasks.map((task) => {
            const failures = task.runs.filter((r) => !r.ok);
            return [
              <tr key={task.key}>
                <td>
                  <Form.Check
                    type="switch"
                    id={`task-${task.key}`}
                    checked={task.enabled}
                    onChange={(e) => toggleTask(task.key, e.target.checked)}
                  />
                </td>
                <td>
                  <div className="fw-semibold">{task.label}</div>
                  <div className="small text-muted">{task.help}</div>
                  {failures.length > 0 ? (
                    <div className="small mt-1 text-danger">
                      <Badge bg="danger" className="me-1 text-uppercase">failed</Badge>
                      last failure {formatDateTime(failures[0].started_at)} — {failures[0].detail}
                    </div>
                  ) : null}
                </td>
                <td className="small">
                  {task.last_ok ? (
                    <>
                      <div>{formatDateTime(task.last_ok.finished_at)}</div>
                      <div className="text-muted">{task.last_ok.detail}</div>
                    </>
                  ) : (
                    <span className="text-muted">never</span>
                  )}
                </td>
                <td className="text-end text-nowrap">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    className="me-1"
                    disabled={task.runs.length === 0}
                    onClick={() => setHistoryKey(historyKey === task.key ? null : task.key)}
                  >
                    History{task.runs.length ? ` (${task.runs.length})` : ""}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled={busyKey !== null}
                    data-hint="Run this task immediately — works even while the switches are off"
                    onClick={() => runNow(task.key)}
                  >
                    {busyKey === task.key ? "Running…" : "Run now"}
                  </Button>
                </td>
              </tr>,
              historyKey === task.key && task.runs.length > 0 ? (
                <tr key={`${task.key}-history`}>
                  <td />
                  <td colSpan={3} className="bg-body-tertiary">
                    {/* The run log, newest first — what the scheduler DID, with
                        each run's task-shaped result (the reopen task lists the
                        job numbers it brought back, as links). */}
                    {task.runs.map((r) => (
                      <div key={`${r.started_at}-${r.ok}`} className="small py-1 border-bottom">
                        <Badge bg={r.ok ? "success" : "danger"} className="me-2 text-uppercase">
                          {r.ok ? "ok" : "failed"}
                        </Badge>
                        {formatDateTime(r.started_at)} — {r.detail}
                        <RunResult result={r.result} />
                      </div>
                    ))}
                  </td>
                </tr>
              ) : null,
            ];
          })}
        </tbody>
      </Table>
    </div>
  );
}

export default SchedulerPanel;
